/**
 * The strong confirmation in front of the one irreversible action this console has.
 *
 * Everything else an operator does here can be undone by somebody: a ban is a row that can be
 * lifted, a demotion is a role that can be given back, a rejected image is a verdict that can be
 * argued with. This deletes a `users` row and everything the schema hangs off it, and nothing
 * anywhere puts it back — so the dialog's whole job is to make sure the operator is looking at
 * the account they meant and knows what the click costs.
 *
 * **THE CONFIRMATION IS A TYPED IDENTIFIER RATHER THAN A CHECKBOX, and that is the point.** A
 * checkbox is a reflex, and a reflex is exactly what deletes the wrong row off a list of fifty
 * near-identical names. `admin.users.remove` takes `confirmDisplayName` for the same reason on
 * its own side — a caller can only know that value by having read the row it is about to delete
 * — so this is the server's second factor drawn, not a second rule invented here. It is compared
 * **case-insensitively**, matching the server exactly (`users_display_name_lower_unique` is what
 * makes a display name identify one account): this is an identity check, not a typing test.
 *
 * The dialog also says what does NOT go, from the server card's own `set null` columns. That is
 * not reassurance — it is the honest half of the sentence: a ban this account issued survives on
 * somebody else's record with the issuer blanked, and other players' match rows survive with the
 * killer blanked, so an operator deleting an ex-moderator is not quietly rewriting history.
 *
 * It is the app's own `Dialog`, unlike the in-arena panels: this is an admin page and a
 * deliberate pause is the entire feature. There is nothing behind it to keep playing.
 */

import { DISPLAY_NAME_MAX_LENGTH } from '@chompdome/shared/schemas';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';

/** The account being deleted, as both callers already hold it off `admin.users.list`/`.detail`. */
export interface DeleteAccountTarget {
  id: string;
  displayName: string;
  email: string | null;
  role: string;
  isGuest: boolean;
  /** `ADMIN_EMAILS` still lists this address, so the account comes back on its next sign-in. */
  allowlisted: boolean;
}

export interface DeleteAccountDialogProps {
  target: DeleteAccountTarget;
  /** The server's refusal, shown verbatim — self and admin-target are the ones operators hit. */
  serverError: string | null;
  pending: boolean;
  onSubmit: (input: { confirmDisplayName: string }) => void;
  onClose: () => void;
}

/** Whether what was typed identifies this account. The server's own comparison, exactly. */
export function confirmsAccount(typed: string, displayName: string): boolean {
  return typed.trim().toLowerCase() === displayName.trim().toLowerCase();
}

export function DeleteAccountDialog({
  target,
  serverError,
  pending,
  onSubmit,
  onClose,
}: DeleteAccountDialogProps) {
  const [typed, setTyped] = useState('');
  const confirmed = confirmsAccount(typed, target.displayName);

  return (
    <Dialog
      open
      title={`Delete ${target.displayName}?`}
      description="Permanent and irreversible. There is no undo, no restore and no archive."
      onClose={onClose}
      className="max-w-lg"
    >
      {/* Named rather than only counted: this dialog is opened from a list of rows that look
          alike, and the row an operator clicked is the one fact they cannot re-check from here. */}
      <dl className="mb-3 grid grid-cols-1 gap-x-6 gap-y-1 rounded-lg border border-border bg-muted/40 p-3 text-sm sm:grid-cols-2">
        <Field label="Account">{target.displayName}</Field>
        <Field label="Email">{target.email ?? 'None (guest)'}</Field>
        <Field label="Role">{target.role}</Field>
        <Field label="Type">{target.isGuest ? 'Guest' : 'Registered'}</Field>
      </dl>

      <p className="text-sm font-semibold text-destructive">This deletes, for good:</p>
      <ul className="mb-3 mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
        <li>the account itself — its id, name, email and role;</li>
        <li>its lifetime totals and personal bests;</li>
        <li>every match it has ever finished, and its rows on every leaderboard with them;</li>
        <li>the images it uploaded and the sayings it submitted;</li>
        <li>every session it is signed in on, here and on any phone.</li>
      </ul>

      {/* The honest half. These are the server's `set null` columns, and they are somebody
          else's history rather than this account's — see `account-deletion.service.ts`. */}
      <p className="text-sm font-medium">What stays:</p>
      <ul className="mb-3 mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
        <li>
          bans this account issued or lifted, on other people&rsquo;s records, with the issuer
          blanked;
        </li>
        <li>other players&rsquo; match rows, with the killer blanked where it was this account.</li>
      </ul>

      {target.allowlisted && (
        <p
          className="mb-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-foreground"
          data-testid="delete-allowlist-warning"
        >
          This address is still in <span className="font-mono">ADMIN_EMAILS</span>. Deleting the
          account does not touch that variable, so the next sign-in from this address mints a
          <em> fresh</em> account — new id, no stats, no matches — and the allowlist grants it admin
          again. Take the address off <span className="font-mono">ADMIN_EMAILS</span> first if you
          mean it to stay gone.
        </p>
      )}

      {target.isGuest && (
        <p className="mb-3 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          This is a guest. A guest row is reclaimed by a device id anybody can send, so what this
          removes is that row&rsquo;s stats, matches and sessions — the player is one refresh away
          from a different guest row and a clean slate.
        </p>
      )}

      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!confirmed) return;
          onSubmit({ confirmDisplayName: typed.trim() });
        }}
      >
        <div className="block text-sm">
          <label className="mb-1 block font-medium" htmlFor="delete-account-confirm">
            Type <span className="font-mono">{target.displayName}</span> to confirm
          </label>
          <Input
            id="delete-account-confirm"
            className="h-9"
            aria-label="Confirm the display name"
            autoComplete="off"
            maxLength={DISPLAY_NAME_MAX_LENGTH}
            value={typed}
            disabled={pending}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>

        {serverError && (
          <p className="text-sm text-destructive" role="alert">
            {serverError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" size="sm" disabled={pending || !confirmed}>
            {pending ? 'Deleting…' : 'Delete for good'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  );
}
